import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN, Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import type { DeviceType } from '@blade/ui-kit'
import type { InviteCode } from '@root/entities/referral'
import type { UserInviteCodeFormModel } from '@root/features/referral/atoms/reatom-use-invite-code-form'

interface InviteCodeFormValues {
  inviteCode: InviteCode | undefined
}

interface InviteCodeFormProps {
  onDone: () => void
  onCancel: () => void
  model: UserInviteCodeFormModel
  existingInviteCode: InviteCode | undefined
  className?: string
  device: DeviceType
}

export const InviteCodeForm = (props: InviteCodeFormProps) => {
  const { model, existingInviteCode, className, device } = props
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)
  const [inviteCode, setInviteCode] = useAtom(model.inviteCodeAtom)
  const [getInviterRequestState] = useAtom(model.getInviterState)

  const [formRequestState] = useAtom(model.formRequestAtom)
  const submitForm = useAction(model.submitFormAction)
  const form = useForm<InviteCodeFormValues>({
    mode: 'all',
    defaultValues: { inviteCode },
  })
  const init = useAction(model.initAction)

  const watchedInviteCode = form.watch('inviteCode')

  useEffect(() => {
    const initSubscription = init()

    return () => {
      initSubscription.unsubscribe()
    }
  }, [init])

  useEffect(() => {
    setInviteCode(watchedInviteCode)
  }, [setInviteCode, watchedInviteCode])

  useEffect(() => {
    AsyncStateN.matchWaiting(getInviterRequestState, {
      Success: () => {
        form.clearErrors('inviteCode')
      },
      Error: () => {
        form.setError('inviteCode', {
          message: t('common.something-went-wrong'),
        })
      },
      Waiting: () => {},
    })
  }, [getInviterRequestState, form, t])

  const handleSubmit_ = form.handleSubmit(async (data) => {
    if (data.inviteCode === undefined) {
      return
    }
    const result = await submitForm({
      inviteCode: data.inviteCode,
    })

    if (Result.isSuccess(result)) {
      props.onDone()
    }
  })

  return (
    <Root className={className} onSubmit={handleSubmit_}>
      <Controller
        name="inviteCode"
        control={form.control}
        disabled={formRequestState.loading}
        rules={{
          required: t('validation.required'),
          maxLength: {
            message: t('validation.max_length', { length: 32 }),
            value: 32,
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            autoFocus
            startContent={
              <StartContent>
                <CurrencyNetworkIcon network={network} size={18} />
                <span>{t('referral.invite-code')}</span>
              </StartContent>
            }
            placeholder={t('referral.enter_unique_code')}
            error={!!fieldState.error?.message}
            size={device === 'desktop' ? 'h36' : 'm'}
          />
        )}
      />

      <Note>
        <Trans
          i18nKey="referral.invite-code-form.note"
          values={{ network: Network.label(network) }}
          components={[<strong key={0} />]}
        />
      </Note>

      <Buttons>
        <Button
          onClick={props.onCancel}
          fullWidth
          radius={'rounded'}
          size={device === 'desktop' ? 'h36' : 'm'}
          variant="soft"
        >
          {t('common.cancel')}
        </Button>
        {existingInviteCode ? (
          <Button
            disabled={
              formRequestState.loading ||
              !form.formState.isValid ||
              !AsyncStateN.isSuccess(getInviterRequestState)
            }
            busy={formRequestState.loading}
            fullWidth
            radius={'rounded'}
            color="primary"
            size={device === 'desktop' ? 'h36' : 'm'}
            type="submit"
          >
            {t('common.save')}
          </Button>
        ) : (
          <Button
            disabled={
              formRequestState.loading ||
              !form.formState.isValid ||
              !AsyncStateN.isSuccess(getInviterRequestState)
            }
            busy={formRequestState.loading || getInviterRequestState.loading}
            fullWidth
            radius={'rounded'}
            color="primary"
            size={device === 'desktop' ? 'h36' : 'm'}
            type="submit"
          >
            {t('referral.add-invite-code')}
          </Button>
        )}
      </Buttons>
    </Root>
  )
}

const Root = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StartContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Buttons = styled.div`
  display: grid;
  gap: 4px;
  grid-template-columns: 1fr 1fr;
`

const Note = styled.p`
  color: var(--textA-6);
  text-align: center;

  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;

  strong {
    color: var(--textA-11);
    font-weight: 400;
  }
`
